<template>
  <div class="px-6 pt-2 pb-0 md:px-16 md:pt-0 md:pb-0 text-center">
    <!-- Out of pocket costs -->
    <h4 class="mb-2 md:mb-0 md:inline-block  md:px-8 md:py-0 text-xl md:text-2xl font-bold md:leading-tight tracking-0.25px text-black-darkest text-center">
      {{ content.estimatedCostText }}
      <p class="hidden md:inline text-primary-dark" data-testid="estimated-costs-value">
        ${{ estimateCostValue }}
      </p>
    </h4>

    <h3 class="md:hidden mb-2 text-center text-34 text-primary-dark font-extrabold">
      ${{ estimateCostValue }}
    </h3>

    <h5 class="md:hidden mb-10 text-center text-xl tracking-0.15px text-primary-dark font-extrabold" data-testid="savings">
      {{ content.savingsText }} ${{ savingsValue }}!
    </h5>

    <div class="md:flex items-center" >
      <div
        class="mb-auto flex-grow"
        :class="{ 'md:mb-20': allowanceAmount === 0 && startDeclineAmount === 0 }"
      >
        <!-- Amount saved -->
        <h5 class="hidden md:inline-block mt-2 px-6 py-0 text-lg tracking-0.15px text-black-darkest font-bold">
          {{ content.savingsText }} <span class="text-primary-dark">${{ savingsValue }}!</span>
        </h5>
        <div v-if="allowanceAmount !== 0" class="relative z-10 mt-8 mx-auto max-w-lg bg-white-high-emphasis px-6 py-4 md:py-6 break-words text-center text-sm leading-relaxed text-black-high-emphasis" v-dompurify-html="global.globalAllowanceContent.globalAllowanceDisclaimer" />
        <div v-else-if="startDeclineAmount !== 0" class="relative z-10 mt-8 mx-auto max-w-lg bg-white-high-emphasis px-6 py-4 md:py-6 break-words text-center text-sm leading-relaxed text-black-high-emphasis" v-dompurify-html="global.declineBalanceContent.declineBalanceDisclaimer" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { EstimatedCostsSection, Question } from '@/model'
import KIcon from '@/components/atoms/Icon/index.vue';
import KButton from '@/components/atoms/Button/index.vue';
import { useFlow, useGlobal } from '@/store'
import { useRoute, useRouter } from 'vue-router'
import { ServiceTypes } from '@/enums'
import KImage from '@/types/KImage'
import useRemoteIcon from '@/hooks/useRemoteIcon'
import { trackEvent } from '@/utils/analytics'
import { addAdobeDataLayerEventEditSelection, trackSelectionEventCJA } from '@/utils/adobe-analytics-data-layer'
import KInformationToolTip from '@/components/molecules/InformationToolTip/index.vue'

export default defineComponent({
  components: {
    KIcon,
    KButton,
    KInformationToolTip
  },
  props: {
    content: {
      type: Object as () => EstimatedCostsSection,
      required: true
    }
  },
  setup (props) {
    const router = useRouter()
    const route = useRoute()
    const global = useGlobal()
    const {
      goToEditSelectionQuestion,
      startFlow,
      flow,
      pricingSummary,
      selectedAnswers,
      serviceType,
      selectionSummary
    } = useFlow()
    const isEditButtonVisible = flow?.hasEditSelectionsQuestion
    const estimateCostValue = pricingSummary.totalMoop
    const savingsValue = pricingSummary.saving
    const allowanceAmount = pricingSummary.allowanceAmount || 0
    const startDeclineAmount = pricingSummary.startDeclineAmount || 0
    const remainingDeclineAmount = pricingSummary.remainingDeclineAmount || 0
    let productImage: KImage | undefined
    
    const restart = () => {
      trackEvent('event-restart')
      trackSelectionEventCJA('event-restart')
      startFlow()
      router.replace({ path: 'flow', query: route.query })
    }

    const editSelections = () => {
      // trackEvent('event-edit-selection')
      addAdobeDataLayerEventEditSelection()
      goToEditSelectionQuestion()
      router.replace({ path: 'flow', query: route.query })
    }

    return {
      estimateCostValue,
      savingsValue,
      allowanceAmount,
      startDeclineAmount,
      remainingDeclineAmount,
      selectionSummary,
      productImage,
      isEditButtonVisible,
      restart,
      editSelections,
      pricingSummary,
      global
    }
  }
})
</script>

<style scoped>
.summary-image {
  @apply mx-auto flex-grow-0 mb-6 px-6;
}

@screen md {
  .summary-image {
    @apply  pl-0 mb-0;
    max-width: 40%;
  }
}

.restart-icon {
  margin-right: 6px;
  @apply my-1 fill-current w-4 h-4;
}
</style>
